import { Fab, Grow, Icon, IconButton } from '@material-ui/core';
import { IStoreState, ThunkDispatch } from '../../../modules';
import { Message, SdkAction, setActiveItem } from '../../../modules/tracelog';
import React, { Component, Fragment, RefObject } from 'react';
import { Theme, WithStyles, createStyles, withStyles } from '@material-ui/core/styles';

import CenteredSecondaryText from '../../../components/CenteredSecondaryText';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import JsonViewer from './JsonViewer';
import MobileSdkViewer from './MobileSdkViewer';
import Paper from '@material-ui/core/Paper';
import PrettyPrintViewer from "./PrettyPrintViewer";
import RawViewer from "./RawViewer";
import Snackbar from '@material-ui/core/Snackbar';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import { bindActionCreators } from 'redux';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import memoize from 'memoize-one';

export type ViewItem = Message | SdkAction

const styles = (theme: Theme) => createStyles({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  tabs: {
    flex: '0 0 auto',
  },
  tabContainer: {
    height: 1,
    flex: '1 1 auto',
  },
  fab: {
    position: 'absolute',
    bottom: theme.spacing(4),
    right: theme.spacing(4),
  },
  fabMoveUp: {
    transform: 'translate3d(0, -46px, 0)',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.enteringScreen,
      easing: theme.transitions.easing.easeOut,
    }),
  },
  fabMoveDown: {
    transform: 'translate3d(0, 0, 0)',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.leavingScreen,
      easing: theme.transitions.easing.sharp,
    }),
  },
  backIcon: {
    position: 'absolute',
    top: 0,
    left: theme.spacing(1),
    zIndex: 10,
    [theme.breakpoints.up('md')]: {
      display: 'none',
    }
  }
})

type State = {
  viewType: number
  content: RefObject<HTMLElement> | null
  visible: boolean
  notifyCopy: boolean
}

class Viewer extends Component<Props, State> {
  state = {
    viewType: 0,
    content: null,
    visible: false,
    notifyCopy: false,
  } as State

  tabChange = (event: React.ChangeEvent<any>, v: number) => {
    this.setState({ viewType: v });
  }
  handleCopy = () => {
    if (this.state.content && this.state.content.current)
      this.triggerCopy(this.state.content.current)
  }
  triggerCopy = (content: HTMLElement) => {
    let selection = window.getSelection()
    if (content && selection !== null) {
      let range = document.createRange()
      range.selectNodeContents(content)
      selection.removeAllRanges()
      selection.addRange(range)
      //add to clipboard
      document.execCommand('copy')
      selection.removeAllRanges()

      this.setState({ notifyCopy: true })
    }
  }
  contentConsumer = (content: RefObject<HTMLElement>) => {
    this.setState({ content })
  }

  viewTypes = memoize((item: ViewItem) => {
    let viewTypes: { label: string, viewer: JSX.Element }[] = []

    if (item && item.class === "sdkaction")
      viewTypes.push({
        label: 'Mobile SDK',
        viewer: <MobileSdkViewer sdkAction={item} contentConsumer={this.contentConsumer} />
      })

    if (item && item.class === "message")
      viewTypes.push({
        label: 'XML',
        viewer: <PrettyPrintViewer message={item} contentConsumer={this.contentConsumer} />
      })

    if (item)
      viewTypes.push({
        label: 'JSON',
        viewer: <JsonViewer item={item} contentConsumer={this.contentConsumer} copyFunction={this.triggerCopy} />
      })

    if (item && item.class === "message")
      viewTypes.push({
        label: 'Raw',
        viewer: <RawViewer message={item} contentConsumer={this.contentConsumer} />
      })
    return viewTypes
  })

  handleClose = () => {
    const { setActiveItem } = this.props
    setActiveItem(null, null)
  }

  componentDidMount() {
    this.setState({ visible: true, viewType: 0 });
  }

  render() {
    const { item, classes } = this.props;

    if (item == null) {
      return (
        <CenteredSecondaryText>Select a record from the left</CenteredSecondaryText>
      )
    }

    return (
      <Fragment>
        <Grow in={this.state.visible}>
          <Paper className={classes.root}>
            <IconButton onClick={this.handleClose} className={classes.backIcon}>
              <FontAwesomeIcon icon={faChevronLeft} />
            </IconButton>
            <Tabs
              value={this.state.viewType}
              onChange={this.tabChange}
              indicatorColor="primary"
              textColor="primary"
              centered
              className={classes.tabs}
            >
              {
                this.viewTypes(item).map((vt, i) =>
                  <Tab label={vt.label} key={i} />
                )
              }
            </Tabs>
            <div className={classes.tabContainer}>
              {
                this.viewTypes(item)[this.state.viewType].viewer
              }
            </div>
            <Fab color="primary" className={classNames(classes.fab, this.state.notifyCopy ? classes.fabMoveUp : classes.fabMoveDown)} onClick={this.handleCopy}>
              <Icon>file_copy</Icon>
            </Fab>
          </Paper>
        </Grow>
        <Snackbar
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          open={this.state.notifyCopy}
          autoHideDuration={2000}
          onClose={() => this.setState({ notifyCopy: false })}
          ContentProps={{
            'aria-describedby': 'message-id',
          }}
          message={<span id="message-id">Copied to clipboard</span>} />
      </Fragment>
    )
  }
}

const mapStateToProps = ({ intl }: IStoreState) => ({
  intl,
})

const mapDispatchToProps = (dispatch: ThunkDispatch) =>
  bindActionCreators(
    {
      setActiveItem,
    }, dispatch
  )

interface Props extends WithStyles<typeof styles>, ReturnType<typeof mapStateToProps>, ReturnType<typeof mapDispatchToProps> {
  item: ViewItem | null
}

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(Viewer))